// https://a.yandex-team.ru/arcadia/taxi/uservices/services/fleet-forms/docs/yaml/api/external.yaml

import {
  useMutation,
  useOnDemandQuery,
  useSuspenseQuery,
  type UseMutationOptions,
  type UseOnDemandQueryOptions,
  type UseSuspenseQueryOptions,
} from "@superweb/api";

import type { ApiRequest, ApiResponse } from "../../api";
import { apiFetch } from "../../fetch";

type UseV1ParkInfoGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/park/info",
  "GET"
>;
type UseV1ParkInfoGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/park/info",
  "GET"
>;
export type UseV1ParkInfoGetResult =
  | {
      status: 200;
      data: Extract<UseV1ParkInfoGetResponse, { status: 200 }>["body"];
    }
  | {
      status: 400;
      error: Extract<UseV1ParkInfoGetResponse, { status: 400 }>["body"];
    }
  | {
      status: 410;
      data: Extract<
        UseV1ParkInfoGetResponse,
        { status: 410 }
      >["body"]["park_info"];
    };

export type UseV1ParkInfoGetParams = Omit<
  UseV1ParkInfoGetRequest,
  "url" | "method"
>;

export type UseV1ParkInfoGetOptions =
  UseSuspenseQueryOptions<UseV1ParkInfoGetResult>;

export const useV1ParkInfoGet = (
  { query, headers }: UseV1ParkInfoGetParams,
  options?: UseV1ParkInfoGetOptions,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/park/info";

  return useSuspenseQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            status: 200 as const,
            data: (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 200 }
            >["body"],
          }),
          400: async (r) => ({
            status: 400 as const,
            error: (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 400 }
            >["body"],
          }),
          410: async (r) => {
            const response = (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 410 }
            >["body"];
            return {
              status: 410 as const,
              data: response.park_info,
            };
          },
        },
      );
    },
    options,
  );
};

export const useOnDemandV1ParkInfoGet = (
  { query, headers }: UseV1ParkInfoGetParams,
  options?: UseOnDemandQueryOptions<UseV1ParkInfoGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/park/info";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            status: 200 as const,
            data: (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 200 }
            >["body"],
          }),
          400: async (r) => ({
            status: 400 as const,
            error: (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 400 }
            >["body"],
          }),
          410: async (r) => {
            const response = (await r.json()) as Extract<
              UseV1ParkInfoGetResponse,
              { status: 410 }
            >["body"];
            return {
              status: 410 as const,
              data: response.park_info,
            };
          },
        },
      );
    },
    options,
  );
};

type UseV1CarBrandsGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/car/brands",
  "GET"
>;
type UseV1CarBrandsGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/car/brands",
  "GET"
>;
export type UseV1CarBrandsGetResult = {
  ok: true;
  data: Extract<UseV1CarBrandsGetResponse, { status: 200 }>["body"];
};

export type UseV1CarBrandsGetParams = Omit<
  UseV1CarBrandsGetRequest,
  "url" | "method"
>;

export const useOnDemandV1CarBrandsGet = (
  { query, headers }: UseV1CarBrandsGetParams,
  options?: UseOnDemandQueryOptions<UseV1CarBrandsGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/car/brands";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            ok: true as const,
            data: (await r.json()) as Extract<
              UseV1CarBrandsGetResponse,
              { status: 200 }
            >["body"],
          }),
        },
      );
    },
    options,
  );
};

type UseV1CarModelsGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/car/models",
  "GET"
>;
type UseV1CarModelsGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/car/models",
  "GET"
>;
export type UseV1CarModelsGetResult =
  | {
      ok: true;
      data: Extract<UseV1CarModelsGetResponse, { status: 200 }>["body"];
    }
  | {
      ok: false;
      error: Extract<UseV1CarModelsGetResponse, { status: 404 }>["body"];
    };

export type UseV1CarModelsGetParams = Omit<
  UseV1CarModelsGetRequest,
  "url" | "method"
>;

export const useOnDemandV1CarModelsGet = (
  { query, headers }: UseV1CarModelsGetParams,
  options?: UseOnDemandQueryOptions<UseV1CarModelsGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/car/models";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            ok: true as const,
            data: (await r.json()) as Extract<
              UseV1CarModelsGetResponse,
              { status: 200 }
            >["body"],
          }),
          404: async (r) => ({
            ok: false as const,
            error: (await r.json()) as Extract<
              UseV1CarModelsGetResponse,
              { status: 404 }
            >["body"],
          }),
        },
      );
    },
    options,
  );
};

type UseV1CarYearsGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/car/years",
  "GET"
>;
type UseV1CarYearsGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/car/years",
  "GET"
>;
export type UseV1CarYearsGetResult = {
  ok: true;
  data: Extract<UseV1CarYearsGetResponse, { status: 200 }>["body"];
};

export type UseV1CarYearsGetParams = Omit<
  UseV1CarYearsGetRequest,
  "url" | "method"
>;

export const useOnDemandV1CarYearsGet = (
  { query, headers }: UseV1CarYearsGetParams,
  options?: UseOnDemandQueryOptions<UseV1CarYearsGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/car/years";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            ok: true as const,
            data: (await r.json()) as Extract<
              UseV1CarYearsGetResponse,
              { status: 200 }
            >["body"],
          }),
        },
      );
    },
    options,
  );
};

type UseV1CarColorsGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/car/colors",
  "GET"
>;
type UseV1CarColorsGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/car/colors",
  "GET"
>;
export type UseV1CarColorsGetResult = {
  ok: true;
  data: Extract<UseV1CarColorsGetResponse, { status: 200 }>["body"];
};

export type UseV1CarColorsGetParams = Omit<
  UseV1CarColorsGetRequest,
  "url" | "method"
>;

export const useOnDemandV1CarColorsGet = (
  { query, headers }: UseV1CarColorsGetParams,
  options?: UseOnDemandQueryOptions<UseV1CarColorsGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/car/colors";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            ok: true as const,
            data: (await r.json()) as Extract<
              UseV1CarColorsGetResponse,
              { status: 200 }
            >["body"],
          }),
        },
      );
    },
    options,
  );
};

type UseV1StationsGetRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/stations",
  "GET"
>;
type UseV1StationsGetResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/stations",
  "GET"
>;
export type UseV1StationsGetResult = {
  data: Extract<UseV1StationsGetResponse, { status: 200 }>["body"];
};

export type UseV1StationsGetParams = Omit<
  UseV1StationsGetRequest,
  "url" | "method"
>;

export const useOnDemandV1StationsGet = (
  { query, headers }: UseV1StationsGetParams,
  options?: UseOnDemandQueryOptions<UseV1StationsGetResult>,
) => {
  const searchParams = new URLSearchParams(query);
  const url = "/api/fleet-forms/selfreg/v1/stations";

  return useOnDemandQuery(
    [url, query, headers],
    () => {
      return apiFetch(
        new Request(`${url}?${searchParams.toString()}`, {
          method: "GET",
          headers,
        }),
        {
          200: async (r) => ({
            data: (await r.json()) as Extract<
              UseV1StationsGetResponse,
              { status: 200 }
            >["body"],
          }),
        },
      );
    },
    options,
  );
};

type UseV1PhoneSubmitPostRequest = ApiRequest<
  "/fleet-forms/selfreg/v1/phone/submit",
  "POST"
>;
type UseV1PhoneSubmitPostResponse = ApiResponse<
  "/fleet-forms/selfreg/v1/phone/submit",
  "POST"
>;
export type UseV1PhoneSubmitPostResult =
  | {
      ok: true;
      data: Extract<UseV1PhoneSubmitPostResponse, { status: 200 }>["body"];
    }
  | {
      ok: false;
      error: Extract<UseV1PhoneSubmitPostResponse, { status: 400 }>["body"];
    };

export type UseV1PhoneSubmitPostParams = Omit<
  UseV1PhoneSubmitPostRequest,
  "url" | "method"
>;

export type UseV1PhoneSubmitPostOptions = UseMutationOptions<
  UseV1PhoneSubmitPostResult,
  unknown,
  UseV1PhoneSubmitPostParams
>;

export const useV1PhoneSubmitPost = (options?: UseV1PhoneSubmitPostOptions) => {
  const url = "/api/fleet-forms/selfreg/v1/phone/submit";

  return useMutation(({ headers, body }) => {
    return apiFetch(
      new Request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      }),
      {
        200: async (r) => ({
          ok: true as const,
          data: (await r.json()) as Extract<
            UseV1PhoneSubmitPostResponse,
            { status: 200 }
          >["body"],
        }),
        400: async (r) => ({
          ok: false as const,
          error: (await r.json()) as Extract<
            UseV1PhoneSubmitPostResponse,
            { status: 400 }
          >["body"],
        }),
      },
    );
  }, options);
};
